/**
 * Typed fetch wrapper for /api/v1/signage/* endpoints.
 *
 * Mirrors the error taxonomy of apps/signage-tv/lib/api/signage_api.dart so
 * the controller can share the same error-handling logic.
 */

export interface PairStartRequest {
  device_code: string;
  platform: 'web';
  screen_resolution: string;
  app_version: string;
  os_version: string;
}

export interface PairStartResponse {
  code: string;
  expires_at: string;
  claim_url: string;
  poll_interval_seconds: number;
}

export interface PairPollRequest {
  device_code: string;
  code: string;
}

export interface PairClaimSuccess {
  device_id: string;
  organization_id: string;
  device_session_token: string;
  expires_at: string;
}

export interface PairClaimPending {
  status: 'pending';
  expires_at: string;
}

export type PairPollResponse = PairClaimSuccess | PairClaimPending;

export interface SignageDeviceCommandAck {
  result: 'success' | 'failure' | 'noop';
  error_message?: string;
  executed_at: string;
}

/* ---------------------------------------------------------------- */
/* Errors                                                           */
/* ---------------------------------------------------------------- */

export class SignageApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'SignageApiError';
    this.status = status;
  }
}

export class NetworkError extends SignageApiError {
  constructor(message = 'network unreachable') {
    super(message, 0);
    this.name = 'NetworkError';
  }
}

export class TokenRevoked extends SignageApiError {
  constructor() {
    super('token revoked', 401);
    this.name = 'TokenRevoked';
  }
}

export class UpgradeRequired extends SignageApiError {
  readonly minimumVersion: string | undefined;
  readonly storeUrl: string | undefined;
  constructor(minimumVersion?: string, storeUrl?: string) {
    super('upgrade required', 426);
    this.name = 'UpgradeRequired';
    this.minimumVersion = minimumVersion;
    this.storeUrl = storeUrl;
  }
}

export class RateLimited extends SignageApiError {
  readonly retryAfterSeconds: number;
  constructor(retryAfterSeconds = 60) {
    super('rate limit exceeded', 429);
    this.name = 'RateLimited';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/* ---------------------------------------------------------------- */
/* Client                                                           */
/* ---------------------------------------------------------------- */

export interface SignageApiOptions {
  readonly baseUrl: string;
  /** Optional fetch override (testing). Defaults to globalThis.fetch. */
  readonly fetchImpl?: typeof fetch;
}

export class SignageApi {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: SignageApiOptions) {
    // Trim trailing slash so path concatenation is straightforward.
    this.baseUrl = opts.baseUrl.replace(/\/+$/, '');
    this.fetchImpl = opts.fetchImpl ?? ((...args) => fetch(...args));
  }

  /** POST /api/v1/signage/pair/start */
  async pairStart(body: PairStartRequest): Promise<PairStartResponse> {
    return this.request<PairStartResponse>('POST', '/api/v1/signage/pair/start', {
      body,
    });
  }

  /** POST /api/v1/signage/pair/poll — long-poll, up to 25s. */
  async pairPoll(body: PairPollRequest, signal?: AbortSignal): Promise<PairPollResponse> {
    return this.request<PairPollResponse>('POST', '/api/v1/signage/pair/poll', {
      body,
      signal,
    });
  }

  /** GET /api/v1/signage/device/config — device-scope auth. */
  async getConfig(token: string, signal?: AbortSignal): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>('GET', '/api/v1/signage/device/config', {
      token,
      signal,
    });
  }

  /** GET /api/v1/signage/device/session-content */
  async getSessionContent(token: string, signal?: AbortSignal): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>(
      'GET',
      '/api/v1/signage/device/session-content',
      { token, signal }
    );
  }

  /** GET /api/v1/signage/device/org-content */
  async getOrgContent(token: string, signal?: AbortSignal): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>('GET', '/api/v1/signage/device/org-content', {
      token,
      signal,
    });
  }

  /** POST /api/v1/signage/device/heartbeat */
  async heartbeat(
    token: string,
    payload: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<void> {
    await this.request<unknown>('POST', '/api/v1/signage/device/heartbeat', {
      body: payload,
      token,
      signal,
    });
  }

  /** GET /api/v1/signage/device/commands — long-poll for pending commands. */
  async getCommands(
    token: string,
    signal?: AbortSignal
  ): Promise<Array<Record<string, unknown>>> {
    const response = await this.request<{ commands?: unknown }>(
      'GET',
      '/api/v1/signage/device/commands',
      { token, signal }
    );
    return response && Array.isArray(response.commands)
      ? response.commands.filter(isRecord)
      : [];
  }

  /** POST /api/v1/signage/device/commands/{id}/ack */
  async ackCommand(
    token: string,
    commandId: string,
    body: SignageDeviceCommandAck,
    signal?: AbortSignal
  ): Promise<void> {
    await this.request<unknown>(
      'POST',
      `/api/v1/signage/device/commands/${encodeURIComponent(commandId)}/ack`,
      { body, token, signal }
    );
  }

  /* ---------------- private ---------------- */

  private async request<T>(
    method: 'GET' | 'POST',
    path: string,
    opts: {
      body?: unknown;
      token?: string;
      signal?: AbortSignal;
    } = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (opts.token) {
      headers['Authorization'] = `Bearer ${opts.token}`;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(this.baseUrl + path, {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: opts.signal,
        credentials: 'omit',
        mode: 'cors',
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err;
      }
      throw new NetworkError(err instanceof Error ? err.message : 'fetch failed');
    }

    if (response.status === 401) {
      throw new TokenRevoked();
    }
    if (response.status === 426) {
      const body = await safeJson(response);
      throw new UpgradeRequired(
        typeof body?.minimum_app_version === 'string' ? body.minimum_app_version : undefined,
        typeof body?.store_url === 'string' ? body.store_url : undefined
      );
    }
    if (response.status === 429) {
      const retry = parseInt(response.headers.get('Retry-After') ?? '60', 10);
      throw new RateLimited(Number.isFinite(retry) ? retry : 60);
    }
    if (!response.ok) {
      throw new SignageApiError(
        `request failed: ${method} ${path} → ${response.status}`,
        response.status
      );
    }
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }
}

async function safeJson(response: Response): Promise<Record<string, unknown> | null> {
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
